import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  FaUserCircle, FaEnvelope, FaPencilAlt, FaCamera, FaSave, FaEdit,
  FaMapMarkerAlt, FaPhone, FaBuilding, FaIdCard, FaCog, FaKey
} from "react-icons/fa";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import axios from "axios";

const Profile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [profileImage, setProfileImage] = useState(null);

  // Enhanced user details with more comprehensive data
  const [userDetails, setUserDetails] = useState({
    firstName: "", // Will be populated from localStorage name
    lastName: "", // Will be populated from localStorage name
    // gender: "Male", // Static for now - COMMENTED OUT
    // country: "United States", // Static for now - COMMENTED OUT
    role: "", // Will be populated from localStorage role
    // timeZone: "UTC-5 (New York)", // Static for now - COMMENTED OUT
    email: "", // Will be populated from localStorage email
    // phone: "+1 (555) 123-4567", // Static for now - COMMENTED OUT
    // department: "Sustainability", // Static for now - COMMENTED OUT
    // jobTitle: "ESG Manager", // Static for now - COMMENTED OUT
    // company: "EcoMina Corp", // Static for now - COMMENTED OUT
    // bio: "Passionate about environmental sustainability and ESG compliance. Leading initiatives to reduce carbon footprint and improve corporate governance.", // Static for now - COMMENTED OUT
    // joinDate: "2023-01-15", // Static for now - COMMENTED OUT
    // lastLogin: "2024-01-15 14:30" // Static for now - COMMENTED OUT
  });

  // Get values from localStorage on component mount
  useEffect(() => {
    const getFromLocalStorage = () => {
      try {
        // These values come from localStorage
        const role = localStorage.getItem('role');
        const email = localStorage.getItem('email');
        const name = localStorage.getItem('name');

        console.log('Retrieved from localStorage:', { role, email, name });

        // Parse name into first and last name
        let firstName = "";
        let lastName = "";

        if (name) {
          const nameParts = name.split(' ');
          firstName = nameParts[0] || "";
          lastName = nameParts.slice(1).join(' ') || "";
        }

        setUserDetails(prev => ({
          ...prev,
          role: role || "Company Admin", // Fallback if localStorage is empty
          email: email || "user@example.com", // Fallback if localStorage is empty
          firstName: firstName || "", // Fallback if localStorage is empty
          lastName: lastName || "" // Fallback if localStorage is empty
        }));

      } catch (error) {
        console.error('Error reading from localStorage:', error);
        toastr.error(t('profile.errorLoadingData'));
      }
    };

    getFromLocalStorage();
  }, []);

  const handleEditClick = async () => {
    if (isEditing) {
      // Save the profile
      await handleSaveProfile();
    } else {
      // Enter edit mode
      setIsEditing(true);
    }
  };

  const handleSaveProfile = async () => {
    try {
      // Validate inputs
      if (!userDetails.firstName.trim()) {
        toastr.error(t('profile.fullNameRequired'));
        return;
      }

      // Get userId from localStorage
      const userId = localStorage.getItem('userId');
      if (!userId) {
        toastr.error(t('profile.userIdNotFound'));
        return;
      }

      // Combine first and last name
      const fullName = `${userDetails.firstName} ${userDetails.lastName}`.trim();

      // Call the API
      const response = await axios.put(`/api/users/${userId}`, {
        name: fullName
      });

      if (response.data.success) {
        // Update localStorage with new name
        localStorage.setItem('name', fullName);

        toastr.success(response.data.message || t('profile.profileUpdated'));
        setIsEditing(false);
      } else {
        toastr.error(response.data.message || t('profile.profileUpdateFailed'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toastr.error(error.response?.data?.message || t('profile.profileUpdateError'));
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toastr.error(t('profile.imageSizeLimit'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result);
        toastr.success(t('profile.imageUpdated'));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    setUserDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
<div className="min-h-screen bg-gray-50 font-sans antialiased">
  {/* Compact Header Section - NOW RESPONSIVE */}
  <div className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 shadow-lg relative overflow-hidden">
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 relative z-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow">
            <FaUserCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-white mb-1">{t('profile.userProfile')}</h1>
            <p className="text-green-100 text-xs sm:text-sm">{t('profile.manageSettings')}</p>
          </div>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-xs text-green-100">{t('profile.memberSince')}: {new Date().toLocaleDateString()}</p>
          <p className="text-sm text-lime-300 font-bold">{t('profile.activeStatus')}: {t('profile.online')}</p>
          <p className="text-xs text-green-200">{t('profile.welcomeBack')}</p>
        </div>
      </div>
    </div>
  </div>

  <div className="max-w-full mx-auto px-3 sm:px-4 py-3 sm:py-4">
    {/* Profile Overview Card */}
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 sm:p-4 mb-4">
      <div className="flex flex-col items-center sm:items-start lg:flex-row lg:items-center gap-4">
        {/* Profile Image Section */}
        <div className="flex-shrink-0">
          <div className="relative">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-500 rounded-full flex items-center justify-center">
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover"
                />
              ) : (
                <FaUserCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              )}
            </div>
            {isEditing && (
              <label
                htmlFor="profileImage"
                className="absolute bottom-0 right-0 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center shadow cursor-pointer hover:bg-gray-50"
              >
                <FaCamera className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-green-600" />
              </label>
            )}
            <input
              type="file"
              id="profileImage"
              className="hidden"
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>
        </div>

        {/* Profile Information */}
        <div className="flex-1 w-full">
          <div className="flex flex-col items-center sm:items-start lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h2 className="text-lg sm:text-lg font-bold text-gray-800 mb-1">
                {userDetails.firstName} {userDetails.lastName}
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-1 sm:space-y-0 text-xs text-gray-500">
                <span className="flex items-center justify-center sm:justify-start">
                  <FaEnvelope className="w-3 h-3 mr-1" />
                  {userDetails.email}
                </span>
              </div>
            </div>
            <div className="flex items-center w-full sm:w-auto justify-center gap-2">
              <button
                onClick={handleEditClick}
                className={`px-4 py-2 w-full sm:w-auto sm:px-3 sm:py-2 rounded-lg flex items-center justify-center gap-2 text-sm ${isEditing
                  ? 'bg-green-500 text-white hover:bg-green-600'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                {isEditing ? (
                  <>
                    <FaSave className="w-3 h-3" />
                    {t('profile.saveChanges')}
                  </>
                ) : (
                  <>
                    <FaEdit className="w-3 h-3" />
                    {t('profile.editProfile')}
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/reset-password')}
                className="px-4 py-2 w-full sm:w-auto sm:px-3 sm:py-2 rounded-lg flex items-center justify-center gap-2 text-sm bg-orange-500 text-white hover:bg-orange-600"
              >
                <FaKey className="w-3 h-3" />
                {t('profile.resetPassword')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Profile Information */}
    <div className="bg-white rounded-lg shadow border border-gray-100 p-3 sm:p-4">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          {/* Personal Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center">
              <FaIdCard className="w-4 h-4 mr-2 text-green-600" />
              {t('profile.personalInfo')}
            </h3>
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 gap-1 sm:gap-0">
                <span className="text-xs text-gray-600">{t('profile.firstName')}</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={userDetails.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="text-sm border rounded px-2 py-1 w-full sm:w-32"
                  />
                ) : (
                  <span className="text-sm font-medium text-right sm:text-left">{userDetails.firstName}</span>
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 gap-1 sm:gap-0">
                <span className="text-xs text-gray-600">{t('profile.lastName')}</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={userDetails.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="text-sm border rounded px-2 py-1 w-full sm:w-32"
                  />
                ) : (
                  <span className="text-sm font-medium text-right sm:text-left">{userDetails.lastName}</span>
                )}
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 flex items-center">
              <FaBuilding className="w-4 h-4 mr-2 text-green-600" />
              {t('profile.professionalInfo')}
            </h3>
            <div className="space-y-2">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 gap-1 sm:gap-0">
                <span className="text-xs text-gray-600">{t('profile.role')}</span>
                <span className="text-sm font-medium text-right sm:text-left">{userDetails.role}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Email Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-800 flex items-center">
            <FaEnvelope className="w-4 h-4 mr-2 text-green-600" />
            {t('profile.contactInfo')}
          </h3>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1 border-b border-gray-100 gap-1 sm:gap-0">
              <span className="text-xs text-gray-600">{t('profile.emailAddress')}</span>
              <span className="text-sm font-medium text-right sm:text-left break-all">{userDetails.email}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
  );
};

export default Profile;